import { useEffect, useMemo, useRef, useState } from "react";
import "./ComparisonChart.css";
import { ChartLegendToggle, useChartLegend } from "./chartLegendToggle";
import { parseNum } from "./trendsFormat";
import { monotonePath, nonNullRuns } from "./chartPaths";

const fmt = (n) => "£" + Math.round(n).toLocaleString("en-GB");
const fmtK = (n) => (n >= 1000 ? "£" + Math.round(n / 1000) + "k" : "£" + Math.round(n));

// Ported verbatim from core-compensation_component.ts's getHistoricalChartColors().
// Functional data-series colors, not typography - kept as-is per the design
// decision (only the font/card system was restyled, not the palette).
const CHART_COLORS = [
  "#FF6B6B", "#4ECDC4", "#45B7D1",
  "#FFEAA7", "#DDA0DD",
  "#98D8C8", "#F7DC6F", "#BB8FCE",
  "#85C1E9", "#F8C471", "#CD6155",
  "#82E0AA", "#F9E79F", "#D7DBDD",
];

// Ported from groupHistoricalDataByTerritory()'s sortOrder - fixed display
// order by territory_id (UK, Ireland, South Africa, Romania, India, Brazil).
const TERRITORY_SORT_ORDER = [4, 5, 2, 3, 1, 6];

function groupByTerritory(rows) {
  const sorted = [...rows].sort(
    (a, b) => TERRITORY_SORT_ORDER.indexOf(a.territory_id) - TERRITORY_SORT_ORDER.indexOf(b.territory_id)
  );
  const groups = {};
  const order = [];
  sorted.forEach((r) => {
    const name = r.territory_name;
    if (!groups[name]) {
      groups[name] = [];
      order.push(name);
    }
    groups[name].push(r);
  });
  return { groups, order };
}

// Ported from getAveragedSeriesData() - averages a field across entries
// sharing the same time period (matches the original's defensive handling
// of potential duplicate rows per period), scaled for the CPI series so it
// can share the left axis visually.
export function averagedSeries(timePeriods, territoryRows, field, scaleFactor = 1) {
  return timePeriods.map(({ year, quarter }) => {
    const entries = territoryRows.filter((r) => r.year === year && r.quarter === quarter);
    const values = entries.map((e) => parseNum(e[field])).filter((v) => v !== null && (field !== "l2" || v > 0));
    if (values.length === 0) return null;
    const avg = values.reduce((sum, v) => sum + v, 0) / values.length;
    return parseFloat((avg * scaleFactor).toFixed(2));
  });
}

// FIXED 2026-08-05: old Chart.js config (core-compensation.component.ts)
// uses `beginAtZero: true` with no fixed max/stepSize - Chart.js's own
// linear scale then auto-picks a "nice" round step (its internal ticks
// algorithm follows the same 1-2-5-per-decade rule as niceNum() below).
// Our SVG port instead divided maxL2*1.1 into 6 equal parts, which lands on
// arbitrary, non-round numbers (e.g. £14k/£28k/£41k instead of £10k/£20k/
// £30k) - confirmed against side-by-side screenshots of the two charts.
// niceNum()/niceScale() replicate that standard "nice numbers for graph
// labels" algorithm (Heckbert), the same technique Chart.js's own linear
// scale is built on, so ticks land on round values like the old chart.
function niceNum(range, round) {
  if (range <= 0) return 1;
  const exponent = Math.floor(Math.log10(range));
  const fraction = range / Math.pow(10, exponent);
  let niceFraction;
  if (round) {
    if (fraction < 1.5) niceFraction = 1;
    else if (fraction < 3) niceFraction = 2;
    else if (fraction < 7) niceFraction = 5;
    else niceFraction = 10;
  } else {
    if (fraction <= 1) niceFraction = 1;
    else if (fraction <= 2) niceFraction = 2;
    else if (fraction <= 5) niceFraction = 5;
    else niceFraction = 10;
  }
  return niceFraction * Math.pow(10, exponent);
}

// beginAtZero: true in the old config - min is always fixed at 0, only the
// max/step are computed from the data range, matching Chart.js's behavior.
function niceScale(maxValue, maxTicks = 8) {
  const safeMax = maxValue > 0 ? maxValue : 1;
  const step = niceNum(safeMax / (maxTicks - 1), true);
  const niceMax = Math.ceil(safeMax / step) * step;
  const tickCount = Math.round(niceMax / step);
  return { max: niceMax, step, tickCount };
}

const W = 1100;
const H = 480;
const M = { top: 24, right: 70, bottom: 60, left: 80 };

export default function ComparisonChart({ filters }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const svgRef = useRef(null);
  const tooltipRef = useRef(null);

  const jobTitleId = filters?.jobTitle?.id;
  // Territory filter (FilterBar's Row 2 multi-select) - client-side only,
  // same approach old Angular used (selectedTerritories.includes(d.territoryId)
  // on the already-fetched data), since /api/historical-compensation
  // returns every territory for the role in one call.
  // FIXED 2026-08-05: was a single filters.territory?.id; Territory is now a
  // multi-select (FilterBar.jsx), so this is an inclusion check against the
  // full array of selected ids instead of an equality check against one.
  // Memoised on the JOINED KEY, not on the array: filters?.territoryIds is a
  // fresh array on every parent render, so depending on it directly would
  // redraw the whole SVG on every render. The key was already used for that
  // reason; deriving the array back from it means the draw effect can depend
  // on a value the linter can verify, instead of needing the dependency
  // check switched off. Same values, same redraw behaviour. (2026-09-18)
  const territoryIdsKey = (filters?.territoryIds ?? []).join(",");
  const territoryIds = useMemo(
    () => (territoryIdsKey ? territoryIdsKey.split(",").map(Number) : []),
    [territoryIdsKey]
  );

  // Only 2024 Q1 onwards, matching the real Angular getHistoricalChartData()
  // filter - done here in the fetch effect since it's a display-layer
  // decision, mirrored from the same choice made in the Flask route.
  useEffect(() => {
    if (!jobTitleId) {
      setRows([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    fetch(`/api/historical-compensation?job_title_id=${jobTitleId}`)
      .then((res) => res.json())
      .then((data) => {
        setRows(data);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [jobTitleId]);

  const visibleRows = territoryIds.length ? rows.filter((r) => territoryIds.includes(r.territory_id)) : rows;
  const { order: legendOrder } = visibleRows.length ? groupByTerritory(visibleRows) : { order: [] };
  const legendKey = loading ? null : JSON.stringify([jobTitleId, [...legendOrder].sort()]);
  const [hidden, toggle] = useChartLegend(legendKey);

  useEffect(() => {
    const svg = svgRef.current;
    const filteredRows = territoryIds.length ? rows.filter((r) => territoryIds.includes(r.territory_id)) : rows;
    if (!svg || filteredRows.length === 0) {
      if (svg) svg.innerHTML = "";
      return;
    }

    const innerW = W - M.left - M.right;
    const innerH = H - M.top - M.bottom;

    const timePeriods = Array.from(
      new Map(filteredRows.map((r) => [`${r.year} Q${r.quarter}`, { year: r.year, quarter: r.quarter, label: r.time_period_name || `${r.year} Q${r.quarter}` }])).values()
    ).sort((a, b) => (a.year === b.year ? a.quarter - b.quarter : a.year - b.year));

    const { groups, order } = groupByTerritory(filteredRows);

    const allL2 = filteredRows.filter((r) => !hidden.has(`${r.territory_name}|comp`)).map((r) => parseNum(r.l2)).filter((v) => v > 0);
    const allCpi = filteredRows.filter((r) => !hidden.has(`${r.territory_name}|cpi`)).map((r) => parseNum(r.avg_cpi)).filter((v) => v !== null);
    const maxL2 = Math.max(...allL2, 1);
    const maxCpi = Math.max(...allCpi, 0);
    const scaleFactor = maxCpi > 0 ? maxL2 / maxCpi : 1;

    const xStep = timePeriods.length > 1 ? innerW / (timePeriods.length - 1) : 0;
    const x = (i) => M.left + i * xStep;
    // beginAtZero + auto "nice" max/step, matching old Chart.js instead of
    // an arbitrary maxL2*1.1 split into 6 equal (non-round) segments.
    const { max: yLeftMax, tickCount: leftTicks } = niceScale(maxL2 * 1.05);
    const yLeft = (v) => M.top + innerH - (v / yLeftMax) * innerH;

    let html = "";

    // Y-axis gridlines + left axis ticks (Compensation £)
    for (let t = 0; t <= leftTicks; t++) {
      const val = (yLeftMax / leftTicks) * t;
      const yPos = M.top + innerH - (val / yLeftMax) * innerH;
      html += `<line class="cc-grid-line" x1="${M.left}" x2="${M.left + innerW}" y1="${yPos}" y2="${yPos}" />`;
      html += `<text class="cc-axis-label" x="${M.left - 10}" y="${yPos + 4}" text-anchor="end">${fmtK(val)}</text>`;
      html += `<text class="cc-axis-label" x="${M.left + innerW + 10}" y="${yPos + 4}" text-anchor="start">${(val / scaleFactor).toFixed(1)}%</text>`;
    }
    html += `<line class="cc-axis-line" x1="${M.left}" x2="${M.left}" y1="${M.top}" y2="${M.top + innerH}"/>`;
    html += `<line class="cc-axis-line" x1="${M.left + innerW}" x2="${M.left + innerW}" y1="${M.top}" y2="${M.top + innerH}"/>`;
    html += `<line class="cc-axis-line" x1="${M.left}" x2="${M.left + innerW}" y1="${M.top + innerH}" y2="${M.top + innerH}"/>`;

    // X-axis labels (time periods) - sparse to avoid crowding
    const labelEvery = Math.ceil(timePeriods.length / 9);
    timePeriods.forEach((tp, i) => {
      if (i % labelEvery === 0 || i === timePeriods.length - 1) {
        html += `<text class="cc-axis-label" x="${x(i)}" y="${M.top + innerH + 20}" text-anchor="middle">${tp.label}</text>`;
      }
    });
    html += `<text class="cc-axis-title" x="${M.left + innerW / 2}" y="${H - 8}" text-anchor="middle">TIME PERIOD</text>`;
    html += `<text class="cc-axis-title" x="${-(M.top + innerH / 2)}" y="18" text-anchor="middle" transform="rotate(-90)">COMPENSATION (GBP)</text>`;
    html += `<text class="cc-axis-title" x="${-(M.top + innerH / 2)}" y="${W - 14}" text-anchor="middle" transform="rotate(-90)">CPI (%)</text>`;

    let colorIndex = 0;
    order.forEach((territoryName) => {
      const territoryRows = groups[territoryName];
      const lineColor = CHART_COLORS[colorIndex % CHART_COLORS.length];
      colorIndex++;
      const cpiColor = CHART_COLORS[colorIndex % CHART_COLORS.length];
      colorIndex++;

      const compSeries = averagedSeries(timePeriods, territoryRows, "l2");
      const cpiSeries = averagedSeries(timePeriods, territoryRows, "avg_cpi", scaleFactor);

      const compPath = nonNullRuns(compSeries, x, yLeft).map(monotonePath).join(" ");
      const cpiPath = nonNullRuns(cpiSeries, x, yLeft).map(monotonePath).join(" ");

      if (!hidden.has(`${territoryName}|comp`)) html += `<path class="cc-line" d="${compPath}" stroke="${lineColor}" data-territory="${territoryName}" data-series="comp"/>`;
      if (!hidden.has(`${territoryName}|cpi`)) html += `<path class="cc-line cc-line-dashed" d="${cpiPath}" stroke="${cpiColor}" data-territory="${territoryName}" data-series="cpi"/>`;

      if (!hidden.has(`${territoryName}|comp`)) compSeries.forEach((v, i) => {
        if (v === null) return;
        html += `<circle class="cc-hover-point" cx="${x(i)}" cy="${yLeft(v)}" r="7" data-territory="${territoryName}" data-series="comp" data-idx="${i}"/>`;
      });
      if (!hidden.has(`${territoryName}|cpi`)) cpiSeries.forEach((v, i) => {
        if (v === null) return;
        html += `<circle class="cc-hover-point" cx="${x(i)}" cy="${yLeft(v)}" r="7" data-territory="${territoryName}" data-series="cpi" data-idx="${i}"/>`;
      });
    });

    svg.innerHTML = html;

    const dataByKey = {};
    order.forEach((territoryName, ti) => {
      const territoryRows = groups[territoryName];
      dataByKey[`${territoryName}|comp`] = { rows: territoryRows, series: averagedSeries(timePeriods, territoryRows, "l2"), color: CHART_COLORS[ti * 2 % CHART_COLORS.length] };
      dataByKey[`${territoryName}|cpi`] = { rows: territoryRows, series: averagedSeries(timePeriods, territoryRows, "avg_cpi", scaleFactor), color: CHART_COLORS[(ti * 2 + 1) % CHART_COLORS.length] };
    });

    svg.querySelectorAll(".cc-hover-point").forEach((pt) => {
      pt.addEventListener("mousemove", (e) => {
        const territory = pt.dataset.territory;
        const series = pt.dataset.series;
        const idx = +pt.dataset.idx;
        const tp = timePeriods[idx];
        const isCpi = series === "cpi";
        const raw = isCpi
          ? averagedSeries(timePeriods, groups[territory], "avg_cpi")[idx]
          : averagedSeries(timePeriods, groups[territory], "l2")[idx];
        const html2 = `<div class="cc-tt-title">${territory}${isCpi ? " CPI" : ""}</div>
          <div class="cc-tt-row"><span class="cc-tt-label">${tp.label}</span><span class="cc-tt-val">${isCpi ? raw.toFixed(2) + "%" : fmt(raw)}</span></div>`;
        showTip(html2, e);
      });
      pt.addEventListener("mouseleave", hideTip);
    });

    function showTip(html2, e) {
      const tt = tooltipRef.current;
      if (!tt) return;
      tt.innerHTML = html2;
      tt.classList.add("on");
      const pad = 14;
      const vw = window.innerWidth, vh = window.innerHeight;
      const w = tt.offsetWidth, h = tt.offsetHeight;
      let left = e.clientX + pad;
      let top = e.clientY + pad;
      if (left + w > vw - 8) left = e.clientX - pad - w;
      if (top + h > vh - 8) top = e.clientY - pad - h;
      tt.style.left = left + "px";
      tt.style.top = top + "px";
    }
    function hideTip() {
      tooltipRef.current?.classList.remove("on");
    }
    return () => {
      hideTip();
      svg.replaceChildren();
    };
  }, [rows, territoryIds, hidden, loading, jobTitleId]);

  return (
    <section className="cc-section">
      <div className="cc-chart-panel">
        <div className="cc-chart-head">
          <div>
            <div className="cc-chart-pretitle">Compensation vs CPI · By territory · GBP</div>
            <div className="cc-chart-title">Comparison chart.</div>
          </div>
        </div>

        {legendOrder.length > 0 && (
          <div className="cc-legend">
            {legendOrder.map((name, i) => (
              <span key={name} className="cc-legend-group">
                <ChartLegendToggle className="cc-legend-item" visible={!hidden.has(`${name}|comp`)} onToggle={() => toggle(`${name}|comp`)}>
                  <span className="cc-swatch-line" style={{ background: CHART_COLORS[i * 2 % CHART_COLORS.length] }} />
                  {name}
                </ChartLegendToggle>
                <ChartLegendToggle className="cc-legend-item" visible={!hidden.has(`${name}|cpi`)} onToggle={() => toggle(`${name}|cpi`)}>
                  <span className="cc-swatch-line cc-swatch-dashed" style={{ borderColor: CHART_COLORS[(i * 2 + 1) % CHART_COLORS.length] }} />
                  {name} CPI
                </ChartLegendToggle>
              </span>
            ))}
          </div>
        )}

        {!jobTitleId && <p className="cc-status">Select a job title to see the comparison chart.</p>}
        {jobTitleId && loading && <p className="cc-status">Loading…</p>}
        {jobTitleId && !loading && visibleRows.length === 0 && <p className="cc-status">No data for this selection.</p>}

        {jobTitleId && !loading && visibleRows.length > 0 && (
          <svg ref={svgRef} className="cc-chart-svg" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="xMidYMid meet" aria-label="Comparison chart" />
        )}
      </div>
      <div ref={tooltipRef} className="cc-tooltip" role="tooltip"></div>
    </section>
  );
}
